(async () => {
  let cartPromise = null;

  async function getCart() {
    if (!cartPromise) {
      cartPromise = fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch cart');
          return res.json();
        })
        .finally(() => {
          cartPromise = null;
        });
    }
    return cartPromise;
  }

  function bindPdpToggle() {
    document.querySelectorAll('.fg-toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const body = btn.closest('.fg-pdp')?.querySelector('.fg-body');
        if (!body) return;
        body.hidden = !body.hidden;
      });
    });
  }

  async function refreshPdpLockState(existingCart) {
    let cart = existingCart;
    if (!cart) {
      try {
        cart = await getCart();
      } catch {
        return;
      }
    }

    const variantIds = cart.items.map((i) => String(i.variant_id));

    document.querySelectorAll('.fg-card').forEach((card) => {
      const status = card.querySelector('.fg-status');
      if (!status) return;

      const parentsRaw = card.dataset.parentVariants || '';
      const parents = parentsRaw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const isUnlocked = parents.some((id) => variantIds.includes(String(id)));

      card.classList.toggle('unlocked', isUnlocked);
      status.innerHTML = isUnlocked
        ? '<img src="https://cdn.shopify.com/s/files/1/0428/4973/files/fg-unlock.svg" width="10" height="12"> Unlocked'
        : '<img src="https://cdn.shopify.com/s/files/1/0428/4973/files/fg-lock.svg" width="8" height="12"> Locked';
    });
  }

  function bindCartBarToggle() {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-fg-cart-bar-toggle]');
      if (!toggle) return;

      const bar = toggle.closest('[data-fg-cart-bar]');
      if (!bar) return;

      const body = bar.querySelector('[data-fg-cart-bar-body]');
      const icon = toggle.querySelector('.fg-cart-bar__toggle-icon');

      if (!body) return;
      const isOpen = !body.hidden;

      if (isOpen) {
        body.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        bar.classList.remove('open', 'active');
      } else {
        body.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        bar.classList.add('open');
      }
    });
  }

  function computeGiftState(cart) {
    const items = cart.items || [];

    const parentsByGiftVariant = {};
    const giftsForParent = {};

    document.querySelectorAll('[data-fg-gift]').forEach((card) => {
      const giftVariantId = String(card.dataset.giftVariant || '');
      const parentsRaw = card.dataset.parentVariants || '';
      const parentIds = parentsRaw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (!giftVariantId || !parentIds.length) return;

      parentsByGiftVariant[giftVariantId] = parentIds;

      parentIds.forEach((pid) => {
        const key = String(pid);
        if (!giftsForParent[key]) giftsForParent[key] = [];
        giftsForParent[key].push({ giftVariantId, element: card });
      });
    });

    const entitlementByGiftVariant = {};
    const claimedByGiftVariant = {};

    items.forEach((item) => {
      const isFreeGift =
        (item.properties && item.properties._is_free_gift === 'true') ||
        (item.properties && item.properties._parent_variant_id != null && item.properties._parent_variant_id !== '') ||
        (Number(item.price) === 0 && (!item.properties || !item.properties._linked_to));
      const variantId = String(item.variant_id);
      const qty = Number(item.quantity) || 0;

      if (isFreeGift) {
        claimedByGiftVariant[variantId] =
          (claimedByGiftVariant[variantId] || 0) + qty;
      } else {
        const gifts = giftsForParent[variantId];
        if (!gifts) return;

        gifts.forEach(({ giftVariantId }) => {
          entitlementByGiftVariant[giftVariantId] =
            (entitlementByGiftVariant[giftVariantId] || 0) + qty;
        });
      }
    });

    return {
      entitlementByGiftVariant,
      claimedByGiftVariant,
      parentsByGiftVariant
    };
  }

  function syncCartGiftUI(cart) {
    const MAX_FREE_GIFTS_PER_VARIANT = 1;
    const { entitlementByGiftVariant, claimedByGiftVariant } =
      computeGiftState(cart);

    document.querySelectorAll('[data-fg-cart-bar]').forEach((bar) => {
      let activeCardsInBar = 0;

      bar.querySelectorAll('[data-fg-gift]').forEach((card) => {
        const giftVariantId = String(card.dataset.giftVariant || '');
        const badge = card.querySelector('[data-fg-gift-entitlement-badge]');
        const claimBtn = card.querySelector('[data-fg-gift-claim]');

        let entitlement = entitlementByGiftVariant[giftVariantId] || 0;
        const claimed = claimedByGiftVariant[giftVariantId] || 0;

        const totalAllowed = entitlement > 0 ? MAX_FREE_GIFTS_PER_VARIANT : 0;
        const remaining = Math.max(0, totalAllowed - claimed);

        if (remaining > 0) {
          activeCardsInBar += 1;
          card.hidden = false;
          card.style.display = '';

          if (badge) {
            badge.textContent = String(remaining);
            badge.hidden = false;
          }

          if (claimBtn) {
            claimBtn.disabled = false;
            claimBtn.classList.remove('fg-cart-gift__btn--disabled');
          }
        } else {
          card.hidden = true;
          card.style.display = 'none';

          if (badge) {
            badge.hidden = true;
          }
          if (claimBtn) {
            claimBtn.disabled = true;
            claimBtn.classList.add('fg-cart-gift__btn--disabled');
          }
        }
      });

      const list = bar.querySelector('[data-fg-gift-list]');
      const toggleBarWrapper = bar.closest('.fg_cart_toggle_bar');

      if (activeCardsInBar > 0) {
        bar.style.display = '';
        bar.hidden = false;
        if (list) list.hidden = false;
        if (toggleBarWrapper) {
          toggleBarWrapper.style.display = '';
          toggleBarWrapper.hidden = false;
        }
      } else {
        bar.style.display = 'none';
        bar.hidden = true;
        if (list) list.hidden = true;
        if (toggleBarWrapper) {
          toggleBarWrapper.style.display = 'none';
          toggleBarWrapper.hidden = true;
        }
      }
    });
  }

  function bindCartGiftActions() {
    const MAX_FREE_GIFTS_PER_VARIANT = 1;

    document.addEventListener('click', async (e) => {
      const claimBtn = e.target.closest('[data-fg-gift-claim]');
      if (!claimBtn) return;

      const card = claimBtn.closest('[data-fg-gift]');
      if (!card) return;

      e.preventDefault();

      const giftVariantId = String(card.dataset.giftVariant || '');
      if (!giftVariantId) return;

      const allBtns = card.querySelectorAll('button');
      allBtns.forEach((b) => (b.disabled = true));

      try {
        const cart = await getCart();
        const {
          entitlementByGiftVariant,
          claimedByGiftVariant,
          parentsByGiftVariant
        } = computeGiftState(cart);

        let entitlement = entitlementByGiftVariant[giftVariantId] || 0;
        const claimed = claimedByGiftVariant[giftVariantId] || 0;

        const totalAllowed = entitlement > 0 ? MAX_FREE_GIFTS_PER_VARIANT : 0;

        let remaining = totalAllowed - claimed;

        if (remaining > 0) {
          const parentCapacities = computeParentCapacities(
            cart,
            giftVariantId,
            parentsByGiftVariant
          );

          const parentIds = Object.keys(parentCapacities);

          while (remaining > 0 && parentIds.length > 0) {
            const parentId = parentIds.find(
              (pid) => parentCapacities[pid] > 0
            );

            if (!parentId) break;

            await addGift(giftVariantId, parentId);
            parentCapacities[parentId] -= 1;
            remaining -= 1;
          }
        }

        const updatedCart = await getCart();
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: updatedCart } })
        );
        document.dispatchEvent(new Event('cart:build'));
        document.dispatchEvent(new CustomEvent('free-gift:cart-updated'));
      } catch (err) {
        console.warn('Free gift action failed', err);
      } finally {
        allBtns.forEach((b) => (b.disabled = false));
      }
    });
  }

  function computeParentCapacities(cart, giftVariantId, parentsByGiftVariant) {
    const items = cart.items || [];
    const parentIds = parentsByGiftVariant[giftVariantId] || [];
    const capacities = {};

    if (!parentIds.length) return capacities;

    const parentQtyByVariant = {};
    items.forEach((item) => {
      const isFreeGift =
        item.properties && item.properties._is_free_gift === 'true';
      if (isFreeGift) return;
      const vid = String(item.variant_id);
      parentQtyByVariant[vid] =
        (parentQtyByVariant[vid] || 0) + (Number(item.quantity) || 0);
    });

    const assignedByParent = {};
    items.forEach((item) => {
      const isFreeGift =
        item.properties && item.properties._is_free_gift === 'true';
      if (!isFreeGift) return;
      if (String(item.variant_id) !== String(giftVariantId)) return;

      const parentId = String(item.properties._parent_variant_id || '');
      if (!parentId) return;

      assignedByParent[parentId] =
        (assignedByParent[parentId] || 0) + (Number(item.quantity) || 0);
    });

    parentIds.forEach((pidRaw) => {
      const pid = String(pidRaw);
      const parentQty = parentQtyByVariant[pid] || 0;
      const assigned = assignedByParent[pid] || 0;
      const remainingForParent = parentQty - assigned;
      if (remainingForParent > 0) {
        capacities[pid] = remainingForParent;
      }
    });

    return capacities;
  }

  async function addGift(variantId, parentId) {
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: {
          _is_free_gift: 'true',
          _parent_variant_id: String(parentId)
        }
      })
    });
    document.dispatchEvent(new CustomEvent('free-gift:cart-updated'));
  }

  async function removeGift(key) {
    // Instant optimistic DOM removal
    document.querySelectorAll(`.cart__item[data-key="${key}"]`).forEach((el) => {
      el.style.display = 'none';
      el.remove();
    });

    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    await fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: 0 })
    });

    try {
      const cart = await getCart();
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart } })
      );
      document.dispatchEvent(new Event('cart:build'));
    } catch (e) {}
  }

  async function autoRemoveOrphanGifts(existingCart) {
    let cart = existingCart;
    if (!cart) {
      try {
        cart = await getCart();
      } catch {
        return false;
      }
    }

    const { entitlementByGiftVariant, parentsByGiftVariant } = computeGiftState(cart);

    const parentVariantIds = new Set(
      cart.items
        .filter((i) => !i.properties || i.properties._is_free_gift !== 'true')
        .map((i) => String(i.variant_id))
    );

    const updates = {};
    let needsUpdate = false;
    const seenGiftVariants = new Set();

    for (const item of cart.items) {
      const isGift =
        item.properties &&
        item.properties._is_free_gift === 'true';

      /*
      * CASE 1
      * Proper free gift from your own flow
      */
      if (isGift) {
        const giftVid = String(item.variant_id);
        const parentId = String(
          item.properties._parent_variant_id || ''
        );
        const validParents = parentsByGiftVariant[giftVid] || [];

        // Check if ANY qualifying product remains in the cart
        const hasParent = (
          (parentId && parentVariantIds.has(parentId)) ||
          validParents.some((pid) => parentVariantIds.has(String(pid))) ||
          ((entitlementByGiftVariant[giftVid] || 0) > 0)
        );

        if (!hasParent || seenGiftVariants.has(giftVid)) {
          updates[item.key] = 0;
          needsUpdate = true;
          // Optimistically remove from DOM immediately
          document.querySelectorAll(`.cart__item[data-key="${item.key}"]`).forEach((el) => {
            el.style.display = 'none';
            el.remove();
          });
          continue;
        }

        seenGiftVariants.add(giftVid);

        if (Number(item.quantity) > 1) {
          updates[item.key] = 1;
          needsUpdate = true;
        }

        continue;
      }

      /*
      * CASE 2
      * Buy Again inserted a gift directly
      * (price 0 but no parent relation)
      */
      const isInstallation =
        item.properties &&
        item.properties._linked_to;

      if (
        Number(item.price) === 0 &&
        !isInstallation &&
        (
          !item.properties ||
          !item.properties._parent_variant_id
        )
      ) {
        updates[item.key] = 0;
        needsUpdate = true;
        document.querySelectorAll(`.cart__item[data-key="${item.key}"]`).forEach((el) => {
          el.style.display = 'none';
          el.remove();
        });
      }
    }

    if (needsUpdate) {
      try {
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
        const res = await fetch(rootPath + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ updates })
        });
        const updatedCart = await res.json();
        syncCartGiftUI(updatedCart);
        refreshPdpLockState(updatedCart);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: updatedCart } })
        );
        document.dispatchEvent(new Event('cart:build'));
      } catch (e) {
        console.warn('Auto remove gift update failed:', e);
      }
      return true;
    }
    return false;
  }

  function bindInstantRemoval() {
    // Intercept clicks on minus/remove buttons in cart to provide instantaneous UI updates
    document.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('.js-qty__adjust--minus');
      if (!minusBtn) return;

      const cartItem = minusBtn.closest('.cart__item');
      if (!cartItem) return;

      const qtyInput = cartItem.querySelector('.js-qty__num');
      const currentQty = parseInt(qtyInput ? qtyInput.value : '1', 10);

      // If user clicked minus on the free gift item itself
      if (cartItem.classList.contains('pro_gift_item_fg')) {
        cartItem.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
        cartItem.style.opacity = '0';
        cartItem.style.pointerEvents = 'none';
        setTimeout(() => {
          cartItem.remove();
        }, 150);
        return;
      }

      // If user is reducing the last parent item to 0
      if (currentQty <= 1) {
        const otherParentItems = Array.from(
          document.querySelectorAll('.cart__item:not(.pro_gift_item_fg)')
        ).filter((item) => item !== cartItem);

        const hasOtherParents = otherParentItems.some((item) => {
          const q = parseInt(item.querySelector('.js-qty__num')?.value || '0', 10);
          return q > 0;
        });

        if (!hasOtherParents) {
          // Immediately hide and remove all free gift elements in DOM
          document.querySelectorAll('.pro_gift_item_fg').forEach((fg) => {
            fg.style.transition = 'opacity 0.15s ease, max-height 0.2s ease';
            fg.style.opacity = '0';
            fg.style.pointerEvents = 'none';
            setTimeout(() => {
              fg.remove();
            }, 150);
          });
        }
      }
    }, true);
  }

  let fgInitDone = false;
  let syncTimeout = null;
  let isSyncing = false;

  function scheduleSync(providedCart) {
    if (syncTimeout) clearTimeout(syncTimeout);

    syncTimeout = setTimeout(async () => {
      syncTimeout = null;
      if (isSyncing) return;
      isSyncing = true;
      try {
        const cart = providedCart || await getCart();
        const changed = await autoRemoveOrphanGifts(cart);
        const finalCart = changed || !providedCart ? await getCart() : cart;
        syncCartGiftUI(finalCart);
        refreshPdpLockState(finalCart);
      } catch (e) {
        console.warn('Free gift sync failed', e);
      } finally {
        isSyncing = false;
      }
    }, 20);
  }

  document.addEventListener('free-gift:cart-updated', () => {
    scheduleSync();
  });

  document.addEventListener('cart:updated', (e) => {
    scheduleSync(e.detail ? e.detail.cart : null);
  });

  function initFreeGift() {
    if (fgInitDone) return;
    fgInitDone = true;

    bindInstantRemoval();
    bindPdpToggle();
    bindCartBarToggle();
    bindCartGiftActions();
    scheduleSync();

    document.addEventListener('variant:change', () => {
      refreshPdpLockState();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFreeGift);
  } else {
    initFreeGift();
  }
})();
